import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/react-hooks';

import type { OperationsQueryType } from '@confluence/site-operations';
import { OperationsQuery } from '@confluence/site-operations';
import { ssrBannerManager } from '@confluence/ssr-utilities';
import type { BannerStateContainer } from '@confluence/banners';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';
import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { useGetNav4OptIn } from '@confluence/nav4-enabled';

export const BANNER_NAME = 'Nav4PromptBanner';
export const MILLISECONDS_IN_48_HOURS = 48 * 60 * 60 * 1000;
// same value used for navHeight in BannerStateContainer in @confluence/banners
const BANNER_HEIGHT = 56;

export const useRegisterNav4PromptBanner = (bannerState: BannerStateContainer | null = null) => {
	const { nav4AdminOptedIn: isNav4SiteWideEnabled } = useGetNav4OptIn();

	const { userId } = useSessionData();
	const { data, error } = useQuery<OperationsQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		OperationsQuery,
		{
			skip: !userId,
		},
	);
	if (error && isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	const isAdmin = data?.siteOperations?.application?.includes('administer');

	// The reminder banner should be displayed before users are a few days away from being forced into nav 4.
	// The only distinction between the two banners is the text.
	const storageKey = fg('confluence_nav4_admin_reminder_banner')
		? PERSISTED_KEYS_ON_SERVER.PERSISTED_NAV4_PROMPT_REMINDER_BANNER_FIRST_DISMISSED
		: PERSISTED_KEYS_ON_SERVER.PERSISTED_NAV4_PROMPT_BANNER_FIRST_DISMISSED;

	const getIsDismissed = () => {
		const dismissedValue = localStorage.getItem(storageKey);
		if (!fg('confluence_nav4_admin_reminder_banner')) return dismissedValue === 'true';

		const dismissedDateTime = new Date(dismissedValue).getTime();
		// Validate storage key value as a date, since we previously stored this value as a true/false string
		if (!isNaN(dismissedDateTime)) {
			// If the banner was dismissed more than 48 hours ago, we want to re-show it
			if (Date.now() - dismissedDateTime > MILLISECONDS_IN_48_HOURS) {
				localStorage.setItem(storageKey, null);
				return false;
			}
			return true;
		} else if (dismissedValue === 'true') {
			// If the banner was previously dismissed as 'true', we want to ensure we maintain the dismissed state and update the local storage value to the current date
			localStorage.setItem(storageKey, new Date().toString());
			return true;
		} else {
			// If the banner was previously not dismissed, we want to ensure we maintain the dismissed state and reset the local storage value
			localStorage.setItem(storageKey, null);
			return false;
		}
	};

	const [isDismissed, setIsDismissed] = useState<boolean>(getIsDismissed());

	const onDismiss = useCallback(() => {
		const dismissedValue = fg('confluence_nav4_admin_reminder_banner')
			? new Date().toString()
			: 'true';
		localStorage.setItem(storageKey, dismissedValue);
		setIsDismissed(true);
		bannerState?.hide(BANNER_NAME);
	}, [bannerState, storageKey]);

	/**
	 * Conditions to show Nav4PromptBanner
	 * - If non-reminder banner, do not show if banner has been previously dismissed
	 * - If reminder banner, do not show if banner has been dismissed in the last 48 hours
	 * - Nav4 GA rollout phase 1
	 * - Nav4 GA phase 2 has not started
	 * - Site admins
	 * - Site wide opt-in is false
	 */
	const shouldRender =
		!isNav4SiteWideEnabled &&
		!isDismissed &&
		isAdmin &&
		fg('confluence_nav4_ga_phase_1') &&
		!fg('confluence_nav4_ga_phase_2');

	if (shouldRender) {
		bannerState?.show(BANNER_NAME, BANNER_HEIGHT);
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			ssrBannerManager.registerBanner({
				name: BANNER_NAME,
				height: BANNER_HEIGHT,
			});
		}
	}

	return {
		shouldRender: Boolean(shouldRender),
		onDismiss,
	};
};
